import {
  PeriodState,
  getSelectedBasisOptionId,
  getIsMultiSelectPeriodFilter,
  isBasisHalfYearly,
  getSelectedPeriodBasis,
  isBasisFullYear,
  getSelectedPeriodType,
  getIsAbsolutePeriodTypeId,
  getIsAbsolutePeriod,
  getIsCalendarBasisId,
  PERIOD_FULL_YEAR_ID,
  ABSOLUTE_BASIS_OPTION_SHORTNAME,
  ABS_PERIOD_TYPE,
  REL_PERIOD_TYPE,
  ACTUAL_CALENDAR_YEAR,
  REL_BASE_YEAR,
} from "@va/core-library/period-filter";

/**
 * It is used to retrieve the list of selected other periods
 * @param {array} otherPeriod other period data
 * @returns {array} array of selected others period list
 */
export function getSelectedOtherPeriodListArray(otherPeriod) {
  return otherPeriod.reduce(
    (previousValue, currentValue) =>
      currentValue.selected
        ? previousValue.concat(currentValue.period_list)
        : previousValue,
    [],
  );
}

/**
 * It is used to get a simple list of currently selected other periods
 * @returns {array} - currently selected other period types
 */
export function getOtherPeriods(period): Array<string> {
  return period && period.other_period
    ? getSelectedOtherPeriodListArray(period.other_period)
    : [];
}

/**
 * It returns the list of cy/fy relative period in order
 * @param {bpplean} isCalBasis whethere basis is calender of financial
 * @param {boolean} relativeYearHasTwoLetterPrefix if true, it will add CY/FY as prefix instead of C/F
 * @returns {string} prefix of basis
 */
export function addBasisPrefix(isCalBasis, relativeYearHasTwoLetterPrefix) {
  let basisPrefix = "";
  if (isCalBasis) {
    basisPrefix = relativeYearHasTwoLetterPrefix ? "CY" : "C";
  } else {
    basisPrefix = relativeYearHasTwoLetterPrefix ? "FY" : "F";
  }
  return basisPrefix;
}

/**
 * It generates a simple list of currently selected period
 * @returns {array} - currently selected periods based on period type
 */
function getAbsPeriodRangeForFullYear(
  fromPeriod,
  toPeriod,
  basisValue,
  otherPeriod,
) {
  let result = [];
  const prefixToAdd = ABSOLUTE_BASIS_OPTION_SHORTNAME[basisValue]["Y"];
  const currentYear = ACTUAL_CALENDAR_YEAR;
  let indexOfOtherPeriod = -1;
  let index = -1;
  for (let i = fromPeriod; i <= toPeriod; i++) {
    index++;
    if (i === currentYear) {
      indexOfOtherPeriod = index + 1;
    }
    result.push(`${prefixToAdd}-${i}`);
  }
  if (otherPeriod && otherPeriod.length) {
    if (indexOfOtherPeriod === -1) {
      if (fromPeriod > currentYear) {
        result = otherPeriod.concat(result);
      } else if (toPeriod < currentYear) {
        result = result.concat(otherPeriod);
      }
    } else {
      result = result
        .slice(0, indexOfOtherPeriod)
        .concat(otherPeriod, result.slice(indexOfOtherPeriod));
    }
  }
  return result;
}

/**
 * It returns the list of cy/fy absolute interim period in order
 * @param {number} fromPeriod from period value
 * @param {number} toPeriod to period value
 * @param {string} basisValue cal/fy
 * @param {string} fromBasisId 1Q/2Q/3Q/4Q/1H/2H
 * @returns {array} period list: list of absolute interim period in a range
 */
function getAbsPeriodRangeForHalfOrQuarterYears(
  fromPeriod,
  toPeriod,
  basisValue,
  fromBasisId,
  toBasisId,
) {
  let result = [];
  const isBasisHalfYear = isBasisHalfYearly(fromBasisId);
  const fromBasisNumber = parseInt(fromBasisId);
  const toBasisNumber = parseInt(toBasisId);
  const basisIdLetter = isBasisHalfYear ? "H" : "Q";
  const endPointAccordingToBasisIsHalfYearlyOrQuarterly = isBasisHalfYear
    ? 2
    : 4;
  for (let i = fromPeriod; i <= toPeriod; i++) {
    const current = [];
    const startPoint = i === fromPeriod ? fromBasisNumber : 1;
    const endPoint =
      i === toPeriod
        ? toBasisNumber
        : endPointAccordingToBasisIsHalfYearlyOrQuarterly;
    for (let j = startPoint; j <= endPoint; j++) {
      const prefixToAdd =
        ABSOLUTE_BASIS_OPTION_SHORTNAME[basisValue][`${j}${basisIdLetter}`];
      current.push(`${prefixToAdd}-${i}`);
    }
    result = result.concat(current);
  }
  return result;
}

/**
 * It returns the list of cy/fy absolute period in order
 * @param {number} fromPeriod from period value
 * @param {number} toPeriod to period value
 * @param {string} basisValue cal/fy
 * @param {string} fromBasisId Y/1Q/2Q/3Q/4Q/1H/2H
 * @returns {array} period list: list of absolute period in a range
 */
function getAbsolutePeriodRange(
  fromPeriod,
  toPeriod,
  basisValue,
  fromBasisId,
  toBasisId,
  otherPeriod,
) {
  const isBasisYearly = isBasisFullYear(fromBasisId);
  if (isBasisYearly) {
    return getAbsPeriodRangeForFullYear(
      fromPeriod,
      toPeriod,
      basisValue,
      otherPeriod,
    );
  }
  return getAbsPeriodRangeForHalfOrQuarterYears(
    fromPeriod,
    toPeriod,
    basisValue,
    fromBasisId,
    toBasisId,
  );
}

/**
 * It returns the list of cy/fy relative period in order
 * @param {number} fromPeriod from period value
 * @param {number} toPeriod to period value
 * @param {string} basisValue cal/fy
 * @param {string} fromBasisId Y/Q/H
 * @param {boolean} hasTwoletterPrefixFoYear if true, it will add CY/FY as prefix instead of C/F
 * @returns {array} period list: list of relative period in a range
 */
function getRelativePeriodYearRange(
  fromPeriod,
  toPeriod,
  basisValue,
  fromBasisId,
  hasTwoletterPrefixFoYear,
  otherPeriod,
) {
  let result = [];
  const isCalBasis = getIsCalendarBasisId(basisValue);
  const basis = addBasisPrefix(isCalBasis, hasTwoletterPrefixFoYear);
  const prefixToAdd = `${basis}${fromBasisId === "Y" ? "" : fromBasisId}`;
  const currentYear = REL_BASE_YEAR;
  let indexOfOtherPeriod = -1;
  let index = -1;
  for (let i = fromPeriod; i <= toPeriod; i++) {
    index++;
    if (i === currentYear) {
      indexOfOtherPeriod = index + 1;
    }
    result.push(`${prefixToAdd}${i}`);
  }
  if (otherPeriod && otherPeriod.length) {
    if (indexOfOtherPeriod === -1) {
      if (fromPeriod > currentYear) {
        result = otherPeriod.concat(result);
      } else if (toPeriod < currentYear) {
        result = result.concat(otherPeriod);
      }
    } else {
      result = result
        .slice(0, indexOfOtherPeriod)
        .concat(otherPeriod, result.slice(indexOfOtherPeriod));
    }
  }
  return result;
}

/**
 * It returns the list of selected period from period meta
 * @param {object} periodMeta period filter object
 * @param {boolean} allPeriodList determines if if should consider is_annual_selected flag and return period according to it or return all the period from-to range
 * @param {boolean} relativeYearHasTwoLetterPrefix if true, it will add CY/FY as prefix instead of C/F
 * @returns {array} - currently selected periods
 */
export function getSelectedPeriods(
  periodMeta,
  allPeriodList = true,
  relativeYearHasTwoLetterPrefix = false,
  includeOtherPeriods: boolean = false,
) {
  if (!periodMeta) {
    return [];
  }
  const selectAllPeriod = allPeriodList ? true : periodMeta.is_annual_selected;
  const otherPeriod = includeOtherPeriods ? getOtherPeriods(periodMeta) : [];
  if (selectAllPeriod) {
    const periodType = getSelectedPeriodType(periodMeta.type);
    const isAbsType = getIsAbsolutePeriodTypeId(periodType);
    const fromPeriod = periodMeta.from_to.selected_from;
    const toPeriod = periodMeta.from_to.selected_to;
    const isMultiSelectPeriodFilter = getIsMultiSelectPeriodFilter(periodMeta);
    const basisValue = getSelectedPeriodBasis(periodMeta.basis);
    let fromBasisId = PERIOD_FULL_YEAR_ID;
    if (
      periodMeta.period_basis &&
      periodMeta.period_basis[periodType] &&
      periodMeta.period_basis[periodType].from_basis
    ) {
      fromBasisId = getSelectedBasisOptionId(
        periodMeta.period_basis[periodType].from_basis,
      );
    }
    let toBasisId = null;
    if (isMultiSelectPeriodFilter) {
      if (
        periodMeta.period_basis &&
        periodMeta.period_basis[periodType] &&
        periodMeta.period_basis[periodType].from_basis
      ) {
        toBasisId = getSelectedBasisOptionId(
          periodMeta.period_basis[periodType].to_basis,
        );
      } else {
        toBasisId = PERIOD_FULL_YEAR_ID;
      }
    }

    if (isMultiSelectPeriodFilter) {
      if (isAbsType) {
        return getAbsolutePeriodRange(
          fromPeriod,
          toPeriod,
          basisValue,
          fromBasisId,
          toBasisId,
          otherPeriod,
        );
      }
      return getRelativePeriodYearRange(
        fromPeriod,
        toPeriod,
        basisValue,
        fromBasisId,
        relativeYearHasTwoLetterPrefix,
        otherPeriod,
      );
    }
    if (isAbsType) {
      return [
        `${ABSOLUTE_BASIS_OPTION_SHORTNAME[basisValue][fromBasisId]}-${fromPeriod}`,
      ];
    }

    const isCalBasis = getIsCalendarBasisId(basisValue);
    const basisPrefix = addBasisPrefix(
      isCalBasis,
      relativeYearHasTwoLetterPrefix,
    );
    const prefixToAdd = `${basisPrefix}${
      fromBasisId === "Y" ? "" : fromBasisId
    }`;
    return [`${prefixToAdd}${fromPeriod}`];
  }
  return otherPeriod;
}

/**
 * It coverts relative period label to relative period id. i.e. converts FY-1 to F-1
 * This is required format for data call payload
 * @returns {string} relativeLabel: relative
 * @returns {string} period id used for data call
 */
export function convertRelativePeriodLabelToId(relativeLabel) {
  return relativeLabel.replace(/Y/g, function () {
    return "";
  });
}

/**
 * It coverts relative period id to relative period label. i.e. converts C-1 to CY-1
 * This is required format for data call payload
 * @returns {string} relativeId: C-1, F-1, CQ-1
 * @returns {string} relative label: CY-1, FY-1, CQ-1
 */
export function convertRelativePeriodIdToLabel(relativeLabel, isYearlyBasis) {
  if (
    relativeLabel === "NTM" ||
    relativeLabel === "STM" ||
    relativeLabel === "LTM" ||
    !isYearlyBasis
  ) {
    return relativeLabel;
  }
  return relativeLabel.replace("C", "CY").replace("F", "FY");
}

/**
 * It is used to retrieve the data id of selected period type
 * @param {string} periodType selected period type
 * @returns {string} dataId of selected period type
 */
export function getSelectedPeriodTypeDataId(periodType) {
  const isAbsolutePeriod = getIsAbsolutePeriod(periodType);
  return isAbsolutePeriod ? ABS_PERIOD_TYPE.dataId : REL_PERIOD_TYPE.dataId;
}

/**
 * It is used to retrieve the data id of selected period type
 * @param {string} period selected period meta data
 * @returns {string} dataId of selected period type
 */
export function getPeriodTypeDataId(period: PeriodState): string {
  return period && period.type
    ? getSelectedPeriodTypeDataId(period.type)
    : ABS_PERIOD_TYPE.dataId;
}
